import { type ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { triggerHaptic } from '@repo/shared/client';
import { Anchor } from '~/components/ui';
import { useAuthForm } from '~/lib/hooks/use-auth-form';
import { AuthForm } from './AuthForm';

// Invite-prefill search params. A team invite link lands here carrying the
// invited address (so the new account is created at that email — onboarding's
// complete step auto-accepts the invite by email match) plus an optional name
// to prefill and the invite id (carried for traceability, not consumed here).
const SignUpSearchSchema = z.object({
  email: z.string().email().optional().catch(undefined),
  name: z.string().min(1).optional().catch(undefined),
  invite: z.string().min(1).optional().catch(undefined),
});
type SignUpSearch = z.infer<typeof SignUpSearchSchema>;

export const Route = createFileRoute('/_auth/sign-up')({
  validateSearch: (search): SignUpSearch => SignUpSearchSchema.parse(search),
  component: SignUpRoute,
});

function SignUpRoute(): ReactNode {
  const { t } = useTranslation();
  const { email, name } = Route.useSearch();
  const navigate = useNavigate();
  const form = useAuthForm('sign-up', name ?? '', email ?? '');

  return (
    <AuthForm
      mode="sign-up"
      {...form}
      errorAction={
        form.emailAlreadyExists ? (
          // Imperative navigate (fully typed) rather than <Link search> — the
          // polymorphic <Anchor component={Link}> wrapper drops the router-specific
          // typing that a typed `search` prop needs.
          <Anchor
            component="button"
            type="button"
            size="sm"
            data-testid="sign-up-existing-account-link"
            onClick={() => {
              triggerHaptic();
              void navigate({
                to: '/sign-in',
                search: { email: form.email.trim() || undefined },
              });
            }}
          >
            {t('auth.signInInstead')}
          </Anchor>
        ) : undefined
      }
      alternateLink={
        <Anchor
          component={Link}
          to="/sign-in"
          size="sm"
          data-testid="sign-up-sign-in-link"
          onClick={() => triggerHaptic()}
        >
          {t('nav.signIn')}
        </Anchor>
      }
    />
  );
}
